import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress, Tabs, Tab } from '@mui/material';
import * as MyCollections from "../services/MyCollections";
import { useAuthStore } from '../store/authStore';
import * as CommonFunctions from '../services/CommonFunctions';
import CustomTooltip from '../components/CustomTooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import Controls from '../components/controls/Controls';
import { useThemeContext } from '../components/contexts/ThemeContext';
import { urlClients, urlAccountants, urlFakturownia, urlAccountantFinancialStatementInvoices } from '../config';
import { fetchDataPromise } from '../services/ApiServices';
import FileUploadCellRenderer from '../components/controls/FileUploadCellRenderer';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import FormDialog from '../components/ClientCard';
import GenerateFinancialStatementsInvoiceDialog from '../components/GenerateFinancialStatementsInvoiceDialog';
import UnsignedStatementModal from '../components/controls/UnsignedStatementModal';
import { hasXmlDigitalSignature } from '../utils/xmlSignatureCheck';
import DeclarationFileCellRenderer from '../components/controls/DeclarationFileCellRenderer';
import AccountantFinancialStatementInvoicesGrid from '../components/AccountantFinancialStatementInvoicesGrid';

const useStyles = makeStyles(theme => ({
    pageContent: {
        marginTop: 20
    },
}))

const initialValue = MyCollections.clientCardInitialValues;

function FinancialStatements({ inlineClientId, popupParentOverride, initialSupervisorMode = false }) {
    const location = useLocation();
    const [redirectedClientId, setRedirectedClientId] = useState(inlineClientId || location.state?.clientId || null);

    useEffect(() => {
        if (inlineClientId) {
            setRedirectedClientId(inlineClientId);
        }
    }, [inlineClientId]);

    const { roles: roles } = useAuthStore();
    const token = useAuthStore().token;
    const userName = useAuthStore().fullName;
    const contactId = useAuthStore().contactId;
    const [gridApi, setGridApi] = useState(null);
    const [tableData, setTableData] = useState([]);
    const classes = useStyles();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const gridRef = useRef();
    const [selectedYear, setSelectedYear] = useState("2025");
    const [activeAccountants, setActiveAccountants] = useState([]);
    const [allAccountants, setAllAccountants] = useState([]);
    const [selectedAccountant, setSelectedAccountant] = useState(0);
    const [filteredData, setFilteredData] = useState(tableData);
    const { mode, toggleTheme } = useThemeContext();
    // When popupParentOverride is provided (e.g. from a Dialog), use it directly.
    // Otherwise always use document.body to avoid popup clipping by overflow:hidden containers.
    const popupParent = popupParentOverride || document.body;
    const tableDataRef = useCallback(node => {
        // Intentionally empty – we no longer point popupParent at the table container
        // because that container can have overflow:hidden in fullscreen mode, clipping popups.
    }, []);
    const darkTheme = mode === 'dark';
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploadedDeclarations, setUploadedDeclarations] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error'); // 'error' | 'success' | 'info' | 'warning'
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [rowIdToDelete, setRowIdToDelete] = useState(null);
    const [deleteType, setDeleteType] = useState('statement'); // 'statement' | 'declaration'
    const values = ['Tak', 'Nie', 'Wybierz...'];
    const today = new Date();
    const nextYear = today.getFullYear() + 1;
    // 31 marca kolejnego roku (pamiętaj: miesiące liczone od 0, więc marzec to 2)
    const bonusEnd = new Date(nextYear, 2, 31);
    const [pendingChange, setPendingChange] = useState(null); // { type, node, rowId, companyName, oldValue, newValue, ... }
    const [open, setOpen] = useState(false);
    const [generatingInvoices, setGeneratingInvoices] = useState(false);
    const [openInvoiceConfirm, setOpenInvoiceConfirm] = useState(false);
    // Ref zamiast state — synchroniczny odczyt w handleRowDataChange (brak race condition)
    const shouldSendRequestRef = useRef(false);
    const suppressAccountantDialogRef = useRef(false);
    // NOWE: tłumienie programowych zmian isSettled i isPaid
    const suppressIsSettledRef = useRef(false);
    const suppressIsClientSettledRef = useRef(false);
    const [recalcVersion, setRecalcVersion] = React.useState(0);
    const isAccountant = roles?.includes('ACCOUNTANT') || roles?.includes('CHIEF_ACCOUNTANT') || roles?.includes('SUPERVISOR_ACCOUNTANT');
    const isSupervisor = roles?.includes('SUPERVISOR') || roles?.includes('SUPERVISOR_ACCOUNTANT');
    const isSupervisorAccountant = roles?.includes('SUPERVISOR_ACCOUNTANT');
    const isAccountManager = roles?.includes('ACCOUNT_MANAGER');
    const isGenerateInvoicesAllowed = roles?.includes('MANAGER') || roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR');
    const [isSupervisorMode, setIsSupervisorMode] = useState(initialSupervisorMode);
    const [clientCardOpen, setClientCardOpen] = useState(false);
    const [formData, setFormData] = useState((JSON.parse(JSON.stringify(initialValue))));

    // Stan modalu dla niepodpisanych spray sprawozdań
    const [unsignedUploadModal, setUnsignedUploadModal] = useState({
        open: false,
        rowId: null,
        clientId: null,
        statementFile: null,
    });

    const [currentTab, setCurrentTab] = useState(0);
    const [isInvoiceUploaded, setIsInvoiceUploaded] = useState(false);
    const [accountantInvoice, setAccountantInvoice] = useState(null);

    const handleSupervisorModeToggle = () => {
        setIsSupervisorMode(prev => !prev);
    };

    const currentModeIsAccountant = useCallback(() => {
        if (isSupervisorAccountant) return !isSupervisorMode; // przełącznik działa
        if (isAccountant) return true; // zwykły księgowy → zawsze tryb księgowego
        return false; // superwizor → nigdy nie jest w trybie księgowego
    }, [isSupervisorAccountant, isSupervisorMode, isAccountant]);

    const currentModeIsSupervisor = useCallback(() => !currentModeIsAccountant(), [currentModeIsAccountant]);

    const checkInvoiceStatus = useCallback(async () => {
        if (!currentModeIsAccountant()) return;

        let actId = selectedAccountant;
        if (actId === 0 || actId === '0') {
            if (isAccountant) {
                const acct = activeAccountants.find(a => a.value === userName || a.value?.includes(userName));
                if (acct) actId = acct.key;
            }
        }

        if (!actId || actId === 0 || actId === '0') {
            setIsInvoiceUploaded(false);
            setAccountantInvoice(null);
            return;
        }

        try {
            const response = await fetch(`${urlAccountantFinancialStatementInvoices}/year/${selectedYear}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const invoice = data.find(inv => Number(inv.accountantId) === Number(actId));
                setIsInvoiceUploaded(!!invoice);
                setAccountantInvoice(invoice || null);
            }
        } catch (error) {
            console.error("Error checking invoice status", error);
        }
    }, [selectedYear, selectedAccountant, isAccountant, activeAccountants, userName, token, currentModeIsAccountant]);

    useEffect(() => {
        checkInvoiceStatus();
    }, [checkInvoiceStatus]);

    const toDoColorMap = {
        DOING_PAY_LATER: '#1b7a21',          // Robimy - płatne później
        DOING_PREPAID: '#1b7a21',            // Robimy - opłacono z góry
        WAITING_FOR_PAYMENT: '#e65100',      // Czekamy na wpłatę
        TO_BE_DEFINED: '#e65100',            // Do ustalenia
        NOT_DOING: '#c62828',                // Nie robimy
        DOING_WAITING_FOR_PROFORMA: '#1b7a21' // Robimy - czekamy na proformę
    };

    // Wersja dla ciemnego motywu — jaśniejsze kolory dla kontrastu na ciemnym tle
    const toDoColorMapDark = {
        DOING_PAY_LATER: '#69c46d',          // jasny zielony
        DOING_PREPAID: '#69c46d',
        WAITING_FOR_PAYMENT: '#ffb74d',      // jasny pomarańczowy
        TO_BE_DEFINED: '#ffb74d',
        NOT_DOING: '#ef5350',                // jasny czerwony
        DOING_WAITING_FOR_PROFORMA: '#69c46d'
    };

    // mapuje id/label -> label
    const getAccountantLabel = useCallback((val) => {
        if (val == null || val === '') return 'brak danych';
        // jeśli to gotowa etykieta
        if (typeof val === 'string' && activeAccountants.some(a => a.value === val)) return val;
        const num = typeof val === 'string' ? Number(val) : val;
        const m = activeAccountants.find(a => a.key === num);
        return m ? m.value : 'brak danych';
    }, [activeAccountants]);

    const handleConfirm = async () => {
        if (!pendingChange) return;

        if (pendingChange.type === "settled" || pendingChange.type === "clientSettled") {
            // ustaw nową wartość bez wywołania kolejnego popupu
            const { node, newValue, type } = pendingChange;
            const fieldName = type === "settled" ? "isSettled" : "isClientSettled";
            if (type === "settled") suppressIsSettledRef.current = true;
            else suppressIsClientSettledRef.current = true;

            shouldSendRequestRef.current = true;
            node.setDataValue(fieldName, newValue);

            // odśwież kolumnę "Sprawozdanie" (warunek isDeleteAllowed zależy od isSettled)
            if (gridRef.current?.api) {
                gridRef.current.api.refreshCells({
                    rowNodes: [node],
                    columns: ["financialStatement"],
                    force: true,
                });
            }
        } else {
            // ...existing code (zmiana księgowej)...
            const { node, toRaw } = pendingChange;
            let newId = null;
            if (typeof toRaw === 'number') newId = toRaw;
            else {
                const m = activeAccountants.find(a => a.value === toRaw) || activeAccountants.find(a => a.key === Number(toRaw));
                newId = m ? m.key : null;
            }
            suppressAccountantDialogRef.current = true;
            shouldSendRequestRef.current = true;
            node.setDataValue('accountantId', newId);
        }

        setPendingChange(null);
        setOpen(false);
    };

    // Anulowanie – nic nie zmieniaj:
    const handleCancel = () => {
        if (pendingChange?.type === "settled" || pendingChange?.type === "clientSettled") {
            // NIC nie cofamy tutaj – widok został już przywrócony wcześniej w onCellValueChanged
        }
        setPendingChange(null);
        setOpen(false);
    };

    const handleInvoiceUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        let actId = selectedAccountant;
        if (actId === 0 || actId === '0') {
            // Spróbuj znaleźć ID księgowego dla obecnego użytkownika, jeśli to księgowy
            if (isAccountant) {
                const acct = activeAccountants.find(a => a.value === userName || a.value?.includes(userName));
                if (acct) actId = acct.key;
            }
        }

        if (!actId || actId === 0 || actId === '0') {
            setSnackbarMessage("Nie wybrano księgowego.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("accountantId", actId);
        formData.append("year", selectedYear);

        fetch(`${urlAccountantFinancialStatementInvoices}/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        })
            .then(response => {
                if (response.ok) {
                    setSnackbarMessage("Faktura została wgrana pomyślnie.");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                    setIsInvoiceUploaded(true);
                    logActivity(`Wgrano fakturę za sprawozdania finansowe (rok ${selectedYear})`, userName, token);
                } else {
                    setSnackbarMessage("Wystąpił błąd podczas wgrywania faktury.");
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                }
            })
            .catch(err => {
                console.error("Błąd przesyłania faktury", err);
                setSnackbarMessage("Błąd sieci podczas wgrywania faktury.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            })
            .finally(() => {
                e.target.value = null; // reset input
            });
    };

    const handleCorrectionUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!accountantInvoice || !accountantInvoice.id) {
            setSnackbarMessage("Brak powiązanej faktury, do której można wgrać korektę.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        fetch(`${urlAccountantFinancialStatementInvoices}/${accountantInvoice.id}/upload-correction`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        })
            .then(response => {
                if (response.ok) {
                    setSnackbarMessage("Korekta została wgrana pomyślnie.");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                    checkInvoiceStatus(); // odśwież dane z backendu
                    logActivity(`Wgrano fakturę korygującą za sprawozdania finansowe (rok ${selectedYear})`, userName, token);
                } else {
                    setSnackbarMessage("Wystąpił błąd podczas wgrywania korekty.");
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                }
            })
            .catch(err => {
                console.error("Błąd przesyłania korekty", err);
                setSnackbarMessage("Błąd sieci podczas wgrywania korekty.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            })
            .finally(() => {
                e.target.value = null; // reset input
            });
    };

    useEffect(() => {
        if (tableData && tableData.length > 0) {
            const initialFiles = {};
            const initialDeclarations = {};

            tableData.forEach(row => {
                if (row.fileMetadataDTO) {
                    initialFiles[row.id] = row.fileMetadataDTO;
                }
                if (row.declarationFileMetadataDTO) {
                    initialDeclarations[row.id] = row.declarationFileMetadataDTO;
                }
            });

            setUploadedFiles(initialFiles);
            setUploadedDeclarations(initialDeclarations);
        }
    }, [tableData]);

    // Centralna walidacja i zapis pliku:
    const handleFileUpload = async (rowId, clientId, file) => {
        if (!rowId || !file) {
            setSnackbarMessage('Brak rekordu lub pliku.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        //Walidacja: tylko .xml
        const name = file.name || '';
        const type = file.type || '';
        const isXml = name.toLowerCase().endsWith('.xml') || type.toLowerCase().includes('xml');

        if (!isXml) {
            setSnackbarMessage('Nieprawidłowy typ pliku — dozwolone są tylko pliki .xml');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // NOWE: weryfikacja podpisu po stronie frontu
        const isSigned = await hasXmlDigitalSignature(file);
        if (!isSigned) {
            // Plik niepodpisany — otwieramy modal zamiast wyświetlać błąd
            setUnsignedUploadModal({ open: true, rowId, clientId, statementFile: file });
            return;
        }

        // Podpisany plik — istniejący flow (bez zmian)
        await doUploadStatement(rowId, clientId, file);
    };

    // Wewnętrzna funkcja uploadu sprawozdania (wspólna dla podpisanych i niepodpisanych)
    const doUploadStatement = async (rowId, clientId, file, unsignedStatement = false) => {
        // Tymczasowo dodaj plik do stanu
        setUploadedFiles(prev => ({
            ...prev,
            [rowId]: { name: file.name, uploading: true }
        }));

        const queryExtra = unsignedStatement ? '&unsignedStatement=true' : '';
        const result = await CommonFunctions.handleFileSubmit(
            file,
            clientId,
            51,
            MyCollections.fileAssignmentId.CLIENT_FINANCIAL_STATEMENT,
            token,
            queryExtra
        );

        const companyName = tableData.find(r => r.id === rowId)?.companyName || 'nieznany klient';

        if (result.success) {
            setUploadedFiles(prev => ({
                ...prev,
                [rowId]: { name: file.name, id: result.fileId }
            }));

            // Zaktualizuj dane wiersza grida (kolumna "Przesłane" czyta z fileMetadataDTO.createdOn)
            if (gridRef.current?.api) {
                const node = gridRef.current.api.getRowNode(rowId);
                if (node?.data) {
                    const updatedRow = {
                        ...node.data,
                        fileMetadataDTO: {
                            ...node.data.fileMetadataDTO,
                            id: result.fileId,
                            filename: file.name,
                            createdOn: new Date().toISOString()
                        }
                    };
                    gridRef.current.api.applyTransaction({
                        update: [updatedRow]
                    });
                    setTableData(prev => prev.map(row => row.id === rowId ? updatedRow : row));
                }
            }

            setSnackbarMessage('Plik pomyślnie dodany.');
            setSnackbarSeverity('success');

            setSnackbarOpen(true);

            // LOG UPLOAD
            logActivity(
                `${logActionType.CLIENTS_FINANCIAL_STATEMENT_ROW_EDIT}, akcja: 'UPLOAD', klient: ${companyName}, plik: '${file.name}'${unsignedStatement ? ', typ: niepodpisane' : ''}`,
                userName,
                token
            );

            return result.fileId;
        } else {
            setUploadedFiles(prev => {
                const next = { ...prev };
                delete next[rowId];
                return next;
            });
            return null;
        }
    };

    // Handler potwierdzenia w modalu niepodpisanego sprawozdania
    const handleUnsignedUploadConfirm = async (statementFile, declarationFile) => {
        const { rowId, clientId } = unsignedUploadModal;

        // 1. Upload sprawozdania (bez podpisu, z flagą)
        const statementFileId = await doUploadStatement(rowId, clientId, statementFile, true);

        // 2. Upload oświadczenia (tylko jeśli sprawozdanie poszło OK)
        if (statementFileId && declarationFile) {
            const result = await CommonFunctions.handleFileSubmit(
                declarationFile,
                clientId,
                61,
                MyCollections.fileAssignmentId.CLIENT_FINANCIAL_STATEMENT,
                token
            );

            if (result.success) {
                // Zaktualizuj stan przycisków w kolumnie "Oświadczenie"
                setUploadedDeclarations(prev => ({
                    ...prev,
                    [rowId]: { name: declarationFile.name, id: result.fileId, fileType: 61 }
                }));

                // Zaktualizuj dane wiersza grida dla oświadczenia
                if (gridRef.current?.api) {
                    const node = gridRef.current.api.getRowNode(rowId);
                    if (node?.data) {
                        const updatedRow = {
                            ...node.data,
                            declarationFileMetadataDTO: {
                                id: result.fileId,
                                filename: declarationFile.name,
                                createdOn: new Date().toISOString()
                            }
                        };
                        gridRef.current.api.applyTransaction({
                            update: [updatedRow]
                        });
                        setTableData(prev => prev.map(row => row.id === rowId ? updatedRow : row));
                    }
                }
            }
        }

        setUnsignedUploadModal({ open: false, rowId: null, clientId: null, statementFile: null });
    };

    const handleFileRemove = (rowId, file) => {
        setFileToDelete(file);
        setRowIdToDelete(rowId);
        setDeleteType('statement');
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        setConfirmOpen(false);
        if (!fileToDelete) return;

        try {
            const file = fileToDelete;
            const rowId = rowIdToDelete;
            const companyName = tableData.find(r => r.id === rowId)?.companyName || 'nieznany klient';

            if (file?.id) {
                const result = await CommonFunctions.deleteFile(file.id, token);
                if (result) {
                    const updateFn = (prev) => {
                        const next = { ...prev };
                        if (Array.isArray(next[rowId])) {
                            next[rowId] = next[rowId].filter(f => f.name !== file.name);
                            if (next[rowId].length === 0) delete next[rowId];
                        } else delete next[rowId];
                        return next;
                    };

                    setUploadedFiles(updateFn);

                    // Wyczyśၺ metadane w danych wiersza grida
                    if (gridRef.current?.api) {
                        const node = gridRef.current.api.getRowNode(rowId);
                        if (node?.data) {
                            const updatedRow = { ...node.data };
                            updatedRow.fileMetadataDTO = null;

                            // Kaskada: jeśli usuwamy sprawozdanie, usuń również oświadczenie
                            const declarationInfo = updatedRow.declarationFileMetadataDTO || uploadedDeclarations[rowId];
                            if (declarationInfo?.id) {
                                try {
                                    await CommonFunctions.deleteFile(declarationInfo.id, token);
                                } catch (e) {
                                    console.warn('Nie udało się usunąć oświadczenia:', e);
                                }
                            }
                            updatedRow.declarationFileMetadataDTO = null;
                            setUploadedDeclarations(prev => {
                                const next = { ...prev };
                                delete next[rowId];
                                return next;
                            });

                            gridRef.current.api.applyTransaction({ update: [updatedRow] });
                            setTableData(prev => prev.map(row => row.id === rowId ? updatedRow : row));
                        }
                    }

                    setSnackbarMessage('Plik został pomyślnie usunięty.');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);

                    // LOG DELETE
                    logActivity(
                        `${logActionType.CLIENTS_FINANCIAL_STATEMENT_ROW_EDIT}, akcja: 'DELETE', klient: ${companyName}, plik: '${file.filename || file.name || 'nieznany'}' (sprawozdanie)`,
                        userName,
                        token
                    );
                }
            } else {
                const updateFn = (prev) => {
                    const next = { ...prev };
                    if (Array.isArray(next[rowId])) {
                        next[rowId] = next[rowId].filter(f => f.name !== file.name);
                        if (next[rowId].length === 0) delete next[rowId];
                    } else delete next[rowId];
                    return next;
                };
                setUploadedFiles(updateFn);
            }
        } catch (error) {
            console.error('Błąd przy usuwaniu pliku:', error);
            setSnackbarMessage('Nie udało się usunąć pliku.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const editorValues = Object.keys(MyCollections.toDoStatusEnumMap);

    // Normalizacja wartości
    const normalizeDeclaredToDo = (value) => {
        if (!value || value === 'Wybierz...') {
            return 'Wybierz...';
        }
        return value;
    };

    // Formatter
    const declaredToDoValueFormatter = (params) => {
        return normalizeDeclaredToDo(params.value);
    };

    // Styl komórki wyglądającej jak select
    const declaredToDoCellStyle = (params, darkTheme) => {
        const value = normalizeDeclaredToDo(params.value);

        // bazowy styl jak select
        const baseStyle = {
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            borderRadius: '4px',
            padding: '4px 8px',
            border: darkTheme ? '1px solid #555' : '1px solid #ccc',
            backgroundColor: darkTheme ? '#2c2c2c' : '#fff',
            cursor: 'pointer'
        };
        const baseStyle1 = {
            color: darkTheme ? toDoColorMapDark[params.value] : toDoColorMap[params.value],
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            borderRadius: '4px',
            padding: '4px 8px',
            border: darkTheme ? '1px solid #555' : '1px solid #ccc',
            backgroundColor: darkTheme ? '#2c2c2c' : '#fff',
            cursor: 'pointer'
        };

        // Kolory tekstu w zależności od wartości
        if (value === 'Tak') {
            return {
                ...baseStyle,
                color: darkTheme ? '#32a439ff' : '#1c9a24ff'
            };
        }

        if (value === 'Nie') {
            return {
                ...baseStyle,
                color: '#d32f2f'
            };
        }

        if (value === 'Wybierz...') {
            return {
                ...baseStyle,
                color: darkTheme ? '#ffb74d' : '#fb8c00',
                fontStyle: 'italic'
            };
        }

        return {
            ...baseStyle1,
        };
    };

    const DeclaredToDoRenderer = (props) => {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: '4px 8px'
            }}>
                <span>{props.value}</span>
                <span style={{ marginLeft: '6px', opacity: 0.6 }}>▼</span>
            </div>
        );
    };

    const columnDefs = useMemo(() => [
        {
            headerName: "Klient",
            field: "companyName",
            editable: false,
            lockPosition: 'left',
            filter: 'agTextColumnFilter',
            minWidth: 220,
            cellStyle: {
                textAlign: 'left',
                fontWeight: "bold",
                backgroundColor: darkTheme ? '#feb33c' : "#ffe599",
                color: darkTheme ? 'black' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                wordBreak: 'break-word',
                lineHeight: '1.2'
            },
            wrapText: true,
            autoHeight: true,
            cellRenderer: function (params) {
                if (!params.data) return null;
                const companyName = params.data?.companyName;

                const handleClick = () => {
                    handleUpdate(params.data);
                };

                return (
                    <div style={{ lineHeight: '1.5' }}>
                        <strong>
                            <span
                                onClick={handleClick}
                                style={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    color: darkTheme ? 'black' : 'black'
                                }}
                            >
                                {companyName}
                            </span>
                        </strong><br />
                    </div>
                );
            },
            tooltipValueGetter: params => params.value || '',
        },
        {
            headerName: "NIP",
            field: "nip",
            lockPosition: 'left',
            filter: 'agTextColumnFilter',
            editable: true,
            minWidth: 125,
            valueGetter: (params) => {
                const nip = params.data?.nip;
                return nip && nip.trim() !== '' ? nip : 'brak...';
            },
            cellStyle: {
                fontWeight: "bold",
                color: darkTheme ? '#a668a5' : '#9c27b0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
            },
        },
        {
            headerName: "Status współpracy",
            field: "cooperationStatus",
            lockPosition: 'left',
            editable: false,
            minWidth: 130,
            filter: 'agTextColumnFilter',

            // 🔹 Wyświetlanie w komórce
            cellRenderer: params => {
                if (!params.value) return "";
                return MyCollections.statusMap[params.value] || params.value;
            },
            filterValueGetter: params =>
                MyCollections.statusMap[params.data?.cooperationStatus] || "",

            cellStyle: params => {
                const commonStyle = {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                };

                let color;
                if (params.value === "SUSPENDED") {
                    color = darkTheme ? "#dab957ff" : '#5f5f42ff';
                } else if (params.value === "ENDED") {
                    color = '#d16752ff';
                } else if (params.value === "ACTIVE") {
                    color = '#26a026ff';
                }

                return { ...commonStyle, color };
            }
        },
        {
            headerName: "Kto robi sprawozdanie?",
            field: "accountantId",
            lockPosition: "left",
            minWidth: 160,
            editable:
                !currentModeIsAccountant() &&
                !roles?.includes("TRADER"),
            hide: currentModeIsAccountant(),
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
                values: [
                    ...activeAccountants
                        .filter(a => a.key !== 0)
                        .map(a => a.value),
                    "brak danych",
                ],
            },
            valueFormatter: params => {
                const id = params.value;
                if (id == null) return "brak danych";
                const match = activeAccountants.find(
                    a => a.key === id || a.value === id
                );
                if (match) return match.value;
                
                const matchAll = allAccountants.find(a => a.key === id || a.value === id);
                return matchAll ? matchAll.value : "brak danych";
            },
            valueParser: params => {
                if (params.newValue === "brak danych") return null;
                const match = activeAccountants.find(a => a.value === params.newValue);
                return match ? match.key : params.newValue;
            },
            onCellValueChanged: (params) => {
                if (suppressAccountantDialogRef.current) {
                    suppressAccountantDialogRef.current = false;
                    return;
                }
                const fromLabel = getAccountantLabel(params.oldValue);
                const toLabel = getAccountantLabel(params.newValue);
                if (fromLabel === toLabel) return;

                setPendingChange({
                    node: params.node,
                    rowId: params.data.id,
                    companyName: params.data.companyName,
                    fromLabel,
                    toLabel,
                    toRaw: params.newValue,
                });

                // cofnij widok do starej wartości do czasu potwierdzenia
                suppressAccountantDialogRef.current = true;
                params.node.setDataValue('accountantId', params.oldValue);
                setOpen(true);
            },
            cellStyle: (params) => declaredToDoCellStyle(params, darkTheme),
            filter: "agTextColumnFilter",
            filterValueGetter: params => {
                const id = params.data?.accountantId;
                if (id == null) return "brak danych";

                const match = activeAccountants.find(a => a.key === id);
                if (match) return match.value;
                
                const matchAll = allAccountants.find(a => a.key === id);
                return matchAll ? matchAll.value : "brak danych";
            },
        },
        {
            headerName: "Czy trzeba robić?",
            field: "toDoStatus",
            lockPosition: 'left',
            filter: 'agTextColumnFilter',
            filterParams: { values: editorValues },
            filterValueGetter: params => {
                return MyCollections.toDoStatusEnumMap[params.data?.toDoStatus] || '';
            },
            editable: !currentModeIsAccountant() && !roles?.includes('TRADER'),
            minWidth: 210,
            cellStyle: (params) => declaredToDoCellStyle(params, darkTheme),
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: editorValues },
            valueFormatter: (params) => MyCollections.toDoStatusEnumMap[params.value] || params.value,
            cellRenderer: (params) => {
                const label = MyCollections.toDoStatusEnumMap[params.value] || params.value || '';
                const textColor = darkTheme
                    ? toDoColorMapDark[params.value]
                    : toDoColorMap[params.value];

                if (!MyCollections.toDoStatusWarning.has(params.value)) {
                    return <span style={{ color: textColor }}>{label}</span>;
                }
                // Pogrubienie fragmentu "(nie wysyłaj!)"
                const warningText = '(nie wysyłaj!)';
                const idx = label.indexOf(warningText);
                if (idx === -1) return <span style={{ color: textColor }}>{label}</span>;
                return (
                    <span style={{ color: textColor }}>
                        {label.substring(0, idx)}
                        <strong style={{ color: '#b63838ff' }}>{warningText}</strong>
                    </span>
                );
            },
        },
        {
            headerName: "Czy przygotujesz SF?",
            field: "isDeclaredToDo",
            lockPosition: 'left',
            filter: 'agSetColumnFilter',
            filterParams: { values },
            editable: currentModeIsAccountant() && (params => params.data?.toDoStatus === 'DOING_PAY_LATER'
                || params.data?.toDoStatus === 'DOING_PREPAID' || params.data?.toDoStatus === 'DOING_WAITING_FOR_PROFORMA'),
            minWidth: 110,
            valueFormatter: declaredToDoValueFormatter,
            cellStyle: (params) => declaredToDoCellStyle(params, darkTheme),
            cellRenderer: DeclaredToDoRenderer,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values },
        },
        {
            headerName: "Stawka",
            field: "numberOfDocuments",
            lockPosition: 'left',
            filter: 'agSetColumnFilter',
            hide: isAccountManager,
            minWidth: 120,
            valueGetter: params => {
                const docs = params.data?.numberOfDocuments || 0;
                const isToDo = params.data?.toDoStatus !== 'NOT_DOING' && params.data?.isDeclaredToDo !== 'Nie';

                if (!isToDo) return "—";
                if (docs <= 300) {
                    return "300 zł netto";
                } else if (docs <= 600) {
                    return "450 zł netto";
                } else {
                    return "600 zł netto";
                }
            },
            cellStyle: params => {
                const docs = params.data?.numberOfDocuments || 0;

                let backgroundColor, color;

                if (darkTheme) {
                    if (docs <= 300) {
                        backgroundColor = '#3b372b'; // muted dark beige
                        color = '#fde48a';
                    } else if (docs <= 600) {
                        backgroundColor = '#4a4333';
                        color = '#ffe9b3';
                    } else {
                        backgroundColor = '#2d5388ff';
                        color = '#fde48a';
                    }
                } else {
                    if (docs <= 300) {
                        backgroundColor = '#fff8e1'; // pale amber
                        color = '#2e7d32';
                    } else if (docs <= 600) {
                        backgroundColor = '#fff3e0'; // light warm
                        color = '#c555b1ff';
                    } else {
                        backgroundColor = '#fffde7';
                        color = '#1565c0';
                    }
                }
                return {
                    backgroundColor,
                    color,
                    transition: 'background-color 0.4s ease, color 0.4s ease',
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    fontWeight: 'bold'
                };
            },
        },
        {
            headerName: "Premia",
            field: "bonusDisplay",
            lockPosition: 'left',
            filter: 'agTextColumnFilter',
            editable: false,
            hide: isAccountManager,
            minWidth: 110,
            valueGetter: params => {
                const yearNum = parseInt(selectedYear);
                const bonusDeadline = new Date(yearNum + 1, 2, 31, 23, 59, 59, 999);

                const fileMetadata = uploadedFiles[params.data.id];
                if (!fileMetadata || fileMetadata.uploading) return "—";

                const createdOn = fileMetadata.createdOn || new Date();

                const isEligible = params.data?.toDoStatus !== 'NOT_DOING' && params.data?.isDeclaredToDo === 'Tak';

                try {
                    const submissionDate = new Date(createdOn);
                    const isOnTime = submissionDate <= bonusDeadline;
                    return isEligible && isOnTime ? "100 zł" : "—";
                } catch (e) {
                    return "—";
                }
            },
            cellStyle: params => ({
                display: 'flex',
                color: darkTheme ? '#fde48a' : '#4527a0',
                backgroundColor: darkTheme ? '#3f3861' : '#ede7f6',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                fontWeight: 'bold',
                borderRadius: '4px'
            }),
            tooltipValueGetter: params => params.value || '',
        },
        {
            headerName: "Do wypłaty (stawka + premia)",
            field: "totalPayout",
            lockPosition: 'left',
            hide: isAccountManager,
            minWidth: 120,
            valueGetter: params => {
                const fileMetadata = uploadedFiles[params.data.id];
                if (!fileMetadata || fileMetadata.uploading) return "—";

                const isEligible = params.data?.toDoStatus !== 'NOT_DOING' && params.data?.isDeclaredToDo === 'Tak';
                if (!isEligible) return "—";

                // Stawka logic
                const docs = params.data?.numberOfDocuments || 0;
                let stawka = 0;
                if (docs <= 300) stawka = 300;
                else if (docs <= 600) stawka = 450;
                else stawka = 600;

                // Premia logic
                const yearNum = parseInt(selectedYear);
                const bonusDeadline = new Date(yearNum + 1, 2, 31, 23, 59, 59, 999);
                let premia = 0;
                try {
                    const createdOn = fileMetadata.createdOn || new Date();
                    const submissionDate = new Date(createdOn);
                    if (submissionDate <= bonusDeadline) premia = 100;
                } catch (e) { }

                return (stawka + premia) + " zł netto";
            },
            cellStyle: params => ({
                display: 'flex',
                color: darkTheme ? '#81c784' : '#2e7d32',
                backgroundColor: darkTheme ? 'rgba(129, 199, 132, 0.1)' : 'rgba(46, 125, 50, 0.05)',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                fontWeight: '800',
                borderRadius: '4px',
                border: darkTheme ? '1px solid rgba(129, 199, 132, 0.2)' : '1px solid rgba(46, 125, 50, 0.1)'
            }),
            tooltipValueGetter: params => params.value || '',
        },
        {
            headerName: "Ile zapłaci klient?",
            field: "averageAmountOfInvoices",
            lockPosition: 'left',
            filter: 'agTextColumnFilter',
            editable: false,
            hide: isAccountant || isSupervisor || isAccountManager,
            minWidth: 125,
            cellStyle: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
            },
            valueGetter: params => {
                let amount = params.data?.averageAmountOfInvoices || 0;
                if (amount > 0) {
                    const finalAmount = Math.max(amount, 738);
                    return finalAmount.toFixed(2) + ' zł brutto';
                } else {
                    return "brak danych...";
                }
            },
            cellRenderer: params => {
                const color = darkTheme ? '#59c357' : '#9c27b0';
                return <span style={{ color, fontWeight: 600 }}>{params.value}</span>;
            },
        },
        {
            headerName: "Faktura",
            field: "generateInvoiceCheckbox",
            lockPosition: 'left',
            hide: !isGenerateInvoicesAllowed,
            suppressMovable: true,
            resizable: false,
            sortable: false,
            filter: false,
            minWidth: 90,
            maxWidth: 90,
            editable: true,
            cellRenderer: 'agCheckboxCellRenderer',
            cellStyle: params => {
                const style = {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                };
                if (params.data?.isInvoiceGenerated) {
                    style.backgroundColor = darkTheme ? "#72a573" : "#b6d7a8";
                    style.color = 'white';
                }
                return style;
            }
        },
        {
            headerName: "Uwagi księgowej",
            field: "accountantNotes",
            lockPosition: 'left',
            minWidth: 150,
            editable: currentModeIsAccountant(),
            cellEditor: 'agLargeTextCellEditor',
            cellEditorPopup: true,
            filter: 'agTextColumnFilter',
            cellEditorParams: { maxLength: 1500 },
            tooltipValueGetter: (params) => params.value || '',
            cellStyle: params => ({
                display: 'block',
                lineHeight: '36px',
                textAlign: 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                backgroundColor: darkTheme ? '#6d6c6cff' : '#ebeaeaff',
                padding: '0 8px'
            }),
            valueFormatter: (params) => {
                if (params.value) {
                    return params.value;
                }
                return 'dodaj tekst...';
            }
        },
        {
            headerName: "Uwagi 7PM",
            field: "internalNotes",
            lockPosition: 'left',
            minWidth: 150,
            hide: currentModeIsAccountant(),
            editable: true,
            cellEditor: 'agLargeTextCellEditor',
            cellEditorPopup: true,
            filter: 'agTextColumnFilter',
            cellEditorParams: { maxLength: 1500 },
            tooltipValueGetter: (params) => params.value || '',
            cellStyle: {
                display: 'block',
                lineHeight: '36px',
                textAlign: 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0 8px'
            },
            valueFormatter: (params) => {
                if (params.value) {
                    return params.value;
                }
                return 'dodaj tekst...';
            }
        },
        // kolumna upload
        {
            headerName: "Sprawozdanie",
            field: "financialStatement",
            lockPosition: 'left',
            minWidth: 160,
            // zamiast funkcji zwracającej JSX – komponent renderer
            cellRenderer: FileUploadCellRenderer,
            cellRendererParams: (params) => ({
                uploadedFiles,
                handleFileUpload,
                handleFileRemove,
                darkTheme,
                token,
                isDeleteAllowed:
                    (currentModeIsAccountant() ||
                        roles?.includes('ADMIN') ||
                        roles?.includes('ADMIN_SUPERVISOR')) && uploadedFiles[params.data.id] != null && !params.data?.isSettled,
                // przycisk widoczny tylko gdy rola ma uprawnienia i nie ma NOT_DOING
                isUploadAllowed:
                    (currentModeIsAccountant() ||
                        roles?.includes('ADMIN') ||
                        roles?.includes('ADMIN_SUPERVISOR')) &&
                    (params.data?.toDoStatus !== 'NOT_DOING' && params.data?.toDoStatus !== 'TO_BE_DEFINED'
                        && params.data?.toDoStatus !== 'WAITING_FOR_PAYMENT' && params.data?.isDeclaredToDo === 'Tak'),
            }),

            cellStyle: {
                display: 'flex',
                lineHeight: '1.7',
                justifyContent: 'center',
                alignItems: 'center',
                whiteSpace: 'normal',
                overflow: 'hidden'
            },
            editable: false,
            sortable: false,
            filter: false,
        },
        {
            headerName: "Oświadczenie",
            field: "declarationFileMetadataDTO",
            lockPosition: 'left',
            minWidth: 150,
            cellRenderer: DeclarationFileCellRenderer,
            cellRendererParams: (params) => ({
                uploadedDeclarations,
                token,
            }),
            cellStyle: {
                display: 'flex',
                lineHeight: '1.7',
                justifyContent: 'center',
                alignItems: 'center',
                whiteSpace: 'normal',
                overflow: 'hidden'
            },
            editable: false,
            sortable: false,
            filter: false,
        },
        {
            headerName: "Przesłane",
            field: "fileMetadataDTO.createdOn",
            lockPosition: 'left',
            minWidth: 110,
            valueGetter: params => {
                const createdOn = params.data?.fileMetadataDTO?.createdOn;
                if (!createdOn) return null;
                try {
                    return new Date(createdOn);
                } catch (e) {
                    return null;
                }
            },
            valueFormatter: params => {
                if (!params.value) return "—";
                return params.value.toLocaleDateString('pl-PL');
            },
            cellStyle: params => {
                const hasValue = params.data?.fileMetadataDTO?.createdOn != null;
                return {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: hasValue ? 'bold' : 'normal',
                    color: hasValue
                        ? (darkTheme ? '#81c784' : '#2e7d32')
                        : (darkTheme ? '#aaa' : '#666')
                };
            },
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterLocalDateAtMidnight, cellValue) => {
                    if (cellValue == null) return -1;
                    const cellDate = new Date(cellValue);
                    const cellDateMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());

                    if (filterLocalDateAtMidnight.getTime() === cellDateMidnight.getTime()) {
                        return 0;
                    }
                    if (cellDateMidnight < filterLocalDateAtMidnight) {
                        return -1;
                    }
                    if (cellDateMidnight > filterLocalDateAtMidnight) {
                        return 1;
                    }
                },
                browserDatePicker: true
            }
        },
        {
            headerName: "Rozliczone z księgowym?",
            field: "isSettled",
            cellRenderer: "agCheckboxCellRenderer",
            cellEditor: "agCheckboxCellEditor",
            lockPosition: "left",
            hide: currentModeIsAccountant() || isAccountManager,
            minWidth: 90,
            editable: params =>
                uploadedFiles[params.data.id] != null,
            suppressMenu: true,
            filter: false,
            sortable: true,
            cellStyle: params => {
                if (!params.data) return null;
                const style = {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                };
                if (params.value) {
                    style.backgroundColor = darkTheme ? "#72a573" : "#b6d7a8";
                    style.color = "white";
                }
                return style;
            },
            // POPRAWIONE: potwierdzenie z tłumieniem i natychmiastowym rollbackiem widoku
            onCellValueChanged: params => {
                // jeśli programowa zmiana – pomiń
                if (suppressIsSettledRef.current) {
                    suppressIsSettledRef.current = false;
                    return;
                }
                if (params.newValue === params.oldValue) return;

                // zakończ edycję
                params.api.stopEditing(false);

                // ustaw pending i cofnij widok do starej wartości, aż do potwierdzenia
                setPendingChange({
                    type: "settled",
                    node: params.node,
                    rowId: params.data.id,
                    companyName: params.data.companyName,
                    oldValue: params.oldValue,
                    newValue: params.newValue,
                });

                suppressIsSettledRef.current = true;
                params.node.setDataValue("isSettled", params.oldValue);

                setOpen(true);
            },
        },
        {
            headerName: "Rozliczone z klientem?",
            field: "isClientSettled",
            cellRenderer: "agCheckboxCellRenderer",
            cellEditor: "agCheckboxCellEditor",
            lockPosition: "left",
            hide: currentModeIsAccountant() || isAccountManager,
            minWidth: 90,
            editable: true,
            suppressMenu: true,
            filter: false,
            sortable: true,
            cellStyle: params => {
                if (!params.data) return null;
                const style = {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                };
                if (params.value) {
                    style.backgroundColor = darkTheme ? "#72a573" : "#b6d7a8";
                    style.color = "white";
                }
                return style;
            },
            onCellValueChanged: params => {
                if (suppressIsClientSettledRef.current) {
                    suppressIsClientSettledRef.current = false;
                    return;
                }
                if (params.newValue === params.oldValue) return;

                params.api.stopEditing(false);

                setPendingChange({
                    type: "clientSettled",
                    node: params.node,
                    rowId: params.data.id,
                    companyName: params.data.companyName,
                    oldValue: params.oldValue,
                    newValue: params.newValue,
                });

                suppressIsClientSettledRef.current = true;
                params.node.setDataValue("isClientSettled", params.oldValue);

                setOpen(true);
            },
        },
        {
            headerName: "Zapłacone?",
            field: "isPaid",
            cellRenderer: "agCheckboxCellRenderer",
            cellEditor: "agCheckboxCellEditor",
            lockPosition: "left",
            hide: currentModeIsAccountant() || isAccountManager,
            editable: true,
            suppressMenu: true,
            filter: false,
            sortable: true,
            minWidth: 90,
            cellStyle: params => {
                if (!params.data) return null;
                const style = {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                };
                if (params.value) {
                    style.backgroundColor = darkTheme ? "#72a573" : "#b6d7a8";
                    style.color = "white";
                }
                return style;
            },
        }
    ], [darkTheme, activeAccountants, uploadedFiles, isSupervisorMode, isAccountManager]);

    const defaultColDef = {
        resizable: true,
        editable: false,
        sortable: true,
        filter: false,
        flex: 1,
        tooltipComponent: CustomTooltip,
    }

    useEffect(() => {
        // Nie wywołujemy tu getClientsWithFinancialStatement — robi to useEffect niżej
        // (deps: [selectedYear]), który odpala się też przy mount. Bez tej zmiany
        // powstawało podwójne żądanie HTTP na starcie strony.
        fetchDataPromise(
            urlAccountants + '/allActiveForSelect',
            mapToSelect,
            (data) => {
                const updatedData = [{ key: 0, value: "Wszyscy księgowi" }, ...data];
                setActiveAccountants(updatedData);
            },
            console.error,
            token
        );
        
        fetchDataPromise(
            urlAccountants + '/allForSelect',
            mapToSelect,
            (data) => setAllAccountants(data),
            console.error,
            token
        );
    }, []);

    useEffect(() => {
        getClientsWithFinancialStatement(selectedYear);
    }, [selectedYear]);

    useEffect(() => {
        if (redirectedClientId) {
            const filtered = tableData.filter(row => row.clientId == redirectedClientId);
            setFilteredData(filtered);
            return;
        }
        if (currentModeIsAccountant()) {
            setSelectedAccountant(contactId);
        }
        if (selectedAccountant === 0 || selectedAccountant === null) {
            setFilteredData(tableData);
            return;
        }
        const filtered = tableData.filter(row => row.accountantId === selectedAccountant && row.toDoStatus !== 'NOT_DOING');
        setFilteredData(filtered);
    }, [selectedAccountant, tableData, isSupervisorMode, redirectedClientId]);

    const getCurrentGridData = React.useCallback(() => {
        const rows = [];
        gridRef.current?.api?.forEachNodeAfterFilter(node => {
            if (node.data) rows.push(node.data);
        });
        return rows;
    }, []);

    useEffect(() => {
        if (gridRef.current?.api) {
            setTimeout(() => {
                gridRef.current?.api?.refreshCells({ force: true });
            }, 0);
        }
    }, [activeAccountants, uploadedFiles]);

    useEffect(() => {
        // Po zmianie danych (np. zmiana księgowego lub wgranie pliku), wymuś przeliczenie sum
        const timeout = setTimeout(() => {
            setRecalcVersion(v => v + 1);
        }, 200);
        return () => clearTimeout(timeout);
    }, [filteredData, uploadedFiles]);

    const mapToSelect = (item) => {
        return {
            key: item.id,
            value: item.surname + ' ' + item.name,
        };
    };


    const handleUpdate = async (oldData) => {
        try {
            const clientData = await fetchDataPromise(urlClients + "/" + oldData.clientId, null, null, console.error, token);
            setFormData(clientData ? clientData : initialValue);
            setClientCardOpen(true);
        } catch (error) {
            console.error('Błąd podczas aktualizacji:', error);
        }
    };

    const handleClose = () => {
        setClientCardOpen(false);
    };

    const onChange = (e) => {
        const { value, id } = e.target
        // console.log(value,id)
        setFormData({ ...formData, [id]: value })
    }

    const handleFormSubmit = () => {
        formData.editedBy = userName;
        const activityType = logActionType.CLIENT_CARD_EDIT;

        if (formData.id) {
            fetch(urlClients + `/${formData.id}`, {
                method: "PUT",
                body: JSON.stringify(formData),
                headers: {
                    'content-type': "application/json",
                    'Authorization': `Bearer ${token}`
                }
            })
                .then(response => {
                    if (!response.ok) {
                        if (response.status === 400) {
                            throw new Error("Klient o podanym numerze NIP już istnieje w bazie");
                        }
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    return true;
                })
                .then(data => {
                    handleClose();
                    logActivity(`${activityType} ${formData.companyWorkingName}, NIP: ${formData.nip}`, userName, token);
                    getClientsWithFinancialStatement(selectedYear);
                })
                .catch(error => {
                    alert(error.message);
                    console.error('Error:', error);
                });
        }
    };


    const getClientsWithFinancialStatement = (selectedYear) => {
        fetch(urlClients + "/allClientsWithFinancialStatement/" + selectedYear, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(resp => resp.json())
            .then(data => {
                setTableData(data.map(item => ({ ...item, generateInvoiceCheckbox: false })));
            })
            .catch(error => {
                console.error('Błąd pobierania danych:', error);
            });
    };

    const handleGenerateInvoices = () => {
        const selectedIds = [];
        gridRef.current.api.forEachNode(node => {
            if (node.data.generateInvoiceCheckbox) {
                selectedIds.push(node.data.id);
            }
        });

        if (selectedIds.length === 0) {
            setSnackbarMessage('Nie wybrano żadnego klienta.');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        setOpenInvoiceConfirm(true);
    };

    const executeGenerateInvoices = async () => {
        const selectedIds = [];
        const selectedNames = [];
        gridRef.current.api.forEachNode(node => {
            if (node.data.generateInvoiceCheckbox) {
                selectedIds.push(node.data.id);
                selectedNames.push(node.data.companyName);
            }
        });

        setGeneratingInvoices(true);
        try {
            // Logowanie rozpoczęcia
            logActivity(
                logActionType.GENERATE_INVOICES_START_STATEMENTS + selectedYear + " dla: " + selectedNames.join(', '),
                userName,
                token
            );

            const response = await fetch(`${urlFakturownia}/generateInvoicesForFinancialStatements`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(selectedIds)
            });

            if (response.ok) {
                const { successIds, errorMessages } = await response.json();

                let displayMessage;
                let logMessage = `Pomyślnie wygenerowano faktury dla ${successIds.length} klientów.`;

                if (errorMessages && errorMessages.length > 0) {
                    logMessage += ` Nie udało się wygenerować faktur dla: ${errorMessages.join(', ')}`;
                    displayMessage = (
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Pomyślnie wygenerowano faktury dla {successIds.length} klientów.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                Nie udało się wygenerować dla:
                            </Typography>
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                                {errorMessages.map((msg, idx) => (
                                    <li key={idx}>{msg}</li>
                                ))}
                            </ul>
                        </Box>
                    );
                } else {
                    displayMessage = logMessage;
                }

                setSnackbarMessage(displayMessage);
                setSnackbarSeverity(errorMessages.length === 0 ? 'success' : 'warning');
                setSnackbarOpen(true);

                // Logowanie zakończenia
                logActivity(
                    logActionType.GENERATE_INVOICES_END_STATEMENTS + selectedYear + ". Wynik: " + logMessage,
                    userName,
                    token
                );

                // Odśwież zaznaczenie w widoku (tylko dla tych, co się udały)
                const updatedRows = [];
                gridRef.current.api.forEachNode(node => {
                    if (node.data.generateInvoiceCheckbox && successIds.includes(node.data.id)) {
                        updatedRows.push({
                            ...node.data,
                            generateInvoiceCheckbox: false,
                            isInvoiceGenerated: true
                        });
                    }
                });

                if (updatedRows.length > 0) {
                    gridRef.current.api.applyTransaction({ update: updatedRows });
                    setTableData(prev => prev.map(row => {
                        const updated = updatedRows.find(u => u.id === row.id);
                        return updated ? updated : row;
                    }));
                }
            } else {
                const errorText = await response.text();
                throw new Error(errorText || 'Błąd serwera');
            }
        } catch (error) {
            setSnackbarMessage('Błąd podczas generowania faktur: ' + error.message);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);

            logActivity(
                `Nie udało się wygenerować faktur dla sprawozdań finansowych ${selectedYear}. Błąd: ${error.message}`,
                userName,
                token
            );
        } finally {
            setGeneratingInvoices(false);
            setOpenInvoiceConfirm(false);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params)
    }

    const clearFilters = useCallback(() => {
        const api = gridRef.current?.api;
        const columnApi = gridRef.current?.columnApi;

        setRedirectedClientId(null);

        if (api) {
            api.setFilterModel(null);

            // Czyszczenie sortowania - obsługa różnych wersji AG Grid
            if (api.applyColumnState) {
                api.applyColumnState({ defaultState: { sort: null } });
            } else if (columnApi && columnApi.applyColumnState) {
                columnApi.applyColumnState({ defaultState: { sort: null } });
            } else if (api.setSortModel) {
                api.setSortModel(null);
            }
        }
    }, []);

    const handleAccountantChange = (e) => {
        setSelectedAccountant(e.target.value);

    };

    const shouldSkipLogForField = (field) => {
        if (field !== 'accountantId' && field !== 'isSettled' && field !== 'isClientSettled') return false;
        // Ref jest synchroniczny — odczyt bezpiecznie po setShouldSendRequestRef.current = true
        if (shouldSendRequestRef.current) {
            shouldSendRequestRef.current = false;
            return false;
        }
        return true;
    }

    const onFirstDataRendered = React.useCallback(() => {
        setTimeout(() => {
            setRecalcVersion(v => v + 1);
        }, 0);
    }, []);

    const getRowId = React.useCallback(params => params.data.id, []);

    const handleRowDataChange = (params) => {
        if (!params) return;
        const { data, colDef, column } = params;

        // Old-school null check to avoid environment-specific optional chaining issues
        const currentColDef = colDef ? colDef : (column && typeof column.getColDef === 'function' ? column.getColDef() : null);

        if (!currentColDef) return;
        const field = currentColDef.field;
        const oldValue = params.oldValue;
        const newValue = params.newValue;

        if (!field) return;

        if (field === 'generateInvoiceCheckbox') return;

        // Ignoruj pierwsze dwa wywołania po załadowaniu danych
        if (shouldSkipLogForField(field)) {
            return;
        }

        // 3) Wyślij zmianę na serwer
        fetch(urlClients + `/cfs/${data.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ ...data, [field]: newValue }),
        })
            .then(resp => resp.json())
            .then(responseData => {
                // 4) Po sukcesie: zaktualizuj tylko ten wiersz w gridzie
                // WAŻNE: nie używamy `...responseData` bezpośrednio, bo serwer może zwrócić
                // null dla pól join (fileMetadataDTO, companyName, nip itp.), co czyściło kolumny.
                // Zamiast tego: bierzemy currentNode.data jako bazę i nakładamy TYLKO
                // niezerowe wartości z responseData.
                if (gridRef.current?.api) {
                    const currentNode = gridRef.current.api.getRowNode(String(data.id));
                    const currentData = currentNode?.data || {};

                    // Pola które ZAWSZE bierzemy z currentData, nigdy z odpowiedzi serwera:
                    // - isDeclaredToDo: backend konwertuje 'Tak'/'Nie'→Boolean przez parseYesNoToBoolean
                    //   i zwraca true/false. Frontend używa stringów. Bezpieczniej całkowicie ignorować
                    //   wartość z serwera i zachować aktualny string z węzła grida.
                    // - generateInvoiceCheckbox: pole lokalne, nie istnieje w bazie
                    // - fileMetadataDTO: pole join, serwer zwraca null po PUT
                    const PRESERVE_FROM_CURRENT = new Set([
                        'isDeclaredToDo',
                        'generateInvoiceCheckbox',
                        'fileMetadataDTO',
                    ]);

                    // Zacznij od pełnego bieżącego stanu wiersza
                    const mergedData = { ...currentData };

                    // Nakładaj z responseData tylko pola, które serwer faktycznie zarządza
                    // (pomijaj null/undefined oraz pola z listy PRESERVE_FROM_CURRENT)
                    if (responseData && typeof responseData === 'object') {
                        for (const [key, val] of Object.entries(responseData)) {
                            if (PRESERVE_FROM_CURRENT.has(key)) continue;
                            if (val !== null && val !== undefined) {
                                mergedData[key] = val;
                            }
                        }
                    }

                    gridRef.current.api.applyTransaction({ update: [mergedData] });

                    // Synchronizuj tableData z mergedData — bez tego zmiana filtra księgowej
                    // resetuje grid do starych danych z tableData (applyTransaction aktualizuje
                    // tylko węzły grida w pamięci, nie stan React)
                    setTableData(prev => prev.map(row => row.id === data.id ? mergedData : row));
                }

                // 🔁 WYZWALACZ PRZELICZENIA
                setRecalcVersion(v => v + 1);

                // 5) Logowanie (używa Twoich helperów)
                const colName = currentColDef?.headerName || currentColDef?.field || 'nieznana kolumna';
                const oldPL = mapValueForLog(field, oldValue);
                const newPL = mapValueForLog(field, newValue);
                logActivity(
                    logActionType.CLIENTS_FINANCIAL_STATEMENT_ROW_EDIT +
                    ", kolumna: '" + colName +
                    "', klient: " + responseData.companyName +
                    ", stara wartość: '" + oldPL +
                    "', nowa wartość: '" + newPL + "'",
                    userName,
                    token
                );
            })
            .catch(error => {
                // 6) Przy błędzie: cofnij zmianę w widoku węzła
                try { if (params?.node && field) params.node.setDataValue(field, oldValue); } catch (e) { /* ignore */ }
                console.error('Błąd podczas aktualizacji danych:', error);
            });
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const baseSalary = React.useMemo(() => {
        const rows = getCurrentGridData();
        if (!rows.length) return 0;

        const eligible = rows.filter(
            r => uploadedFiles[r.id] != null
        );

        return eligible.reduce((sum, row) => {
            const docs = row.numberOfDocuments || 0;

            if (docs <= 300) return sum + 300;
            if (docs <= 600) return sum + 450;
            return sum + 600;
        }, 0);
    }, [recalcVersion, getCurrentGridData]);

    const clientTotal = React.useMemo(() => {
        const rows = getCurrentGridData();
        if (!rows.length) return 0;

        const eligible = rows.filter(
            r => uploadedFiles[r.id] != null
        );

        return eligible.reduce((sum, row) => {
            const amount = row.averageAmountOfInvoices || 0;
            const finalAmount = amount > 0 ? Math.max(amount, 738) : 0;
            return sum + finalAmount;
        }, 0);
    }, [recalcVersion, getCurrentGridData]);

    const bonusAmount = React.useMemo(() => {
        const rows = getCurrentGridData();
        if (!rows.length) return 0;

        const yearNum = parseInt(selectedYear);
        // Deadline to 31 marca roku następnego (np. dla 2025 to 31.03.2026)
        const bonusDeadline = new Date(yearNum + 1, 2, 31, 23, 59, 59, 999);

        const eligible = rows.filter(r => {
            // Podstawowe warunki: robimy i zadeklarowano "Tak"
            if (r.toDoStatus === 'NOT_DOING' || r.isDeclaredToDo !== 'Tak') return false;

            // Warunek terminowości: data utworzenia pliku musi być <= deadline
            const fileMetadata = uploadedFiles[r.id];
            if (!fileMetadata || fileMetadata.uploading) return false;

            try {
                const createdOn = fileMetadata.createdOn || new Date();
                const submissionDate = new Date(createdOn);
                return submissionDate <= bonusDeadline;
            } catch (e) {
                return false;
            }
        });

        return eligible.length * 100;
    }, [recalcVersion, getCurrentGridData, selectedYear]);



    // Mapowanie wartości do logowania (PL)
    const mapValueForLog = (field, value) => {
        if (field === 'accountantId') {
            return getAccountantLabel(value); // imię i nazwisko lub 'brak danych'
        }
        if (field === 'toDoStatus') {
            return MyCollections.toDoStatusEnumMap[value] || 'brak danych';
        }
        if (field === 'isSettled') {
            if (value === true) return 'Rozliczone';
            return 'Nierozliczone';
        }
        if (field === 'isClientSettled') {
            if (value === true) return 'Rozliczone';
            return 'Nierozliczone';
        }
        if (field === 'isPaid') {
            if (value === true) return 'Zapłacone';
            return 'Niezapłacone';
        }
        if (value === null || value === undefined || value === '') return 'brak danych';
        return String(value);
    };

    const handleYearChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedYear(selectedValue);
    };

    return (
        <>
            <Paper className={classes.pageContent} elevation={0}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap="12px"
                    sx={{ mb: 2, px: 2, pt: 2 }}
                >
                    {/* Lewa strona: Filtry */}
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                        {!inlineClientId && (
                            <Box minWidth={140}>
                                <Controls.Select
                                    name="accountant"
                                    label="Księgowy:"
                                    value={selectedAccountant}
                                    onChange={handleAccountantChange}
                                    options={activeAccountants}
                                    size="small"
                                    disabled={currentModeIsAccountant()}
                                />
                            </Box>
                        )}
                        <Box minWidth={100}>
                            <Controls.MySelect
                                name="yearId"
                                label="Rok:"
                                value={selectedYear}
                                onChange={handleYearChange}
                                options={MyCollections.getYearCollection(
                                    2023,
                                    CommonFunctions.getYearForSelect()
                                )}
                            />
                        </Box>
                    </Box>

                    {/* Prawa strona: Przyciski akcji */}
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap="8px">
                        {!inlineClientId && isSupervisorAccountant && (
                            <Controls.PremiumButton
                                variant="glass"
                                label={isSupervisorMode ? 'Tryb księgowego' : 'Tryb superwizora'}
                                onClick={handleSupervisorModeToggle}
                                icon={isSupervisorMode ? 'fas fa-user-edit' : 'fas fa-user-shield'}
                                color={isSupervisorMode ? 'secondary' : 'success'}
                            />
                        )}
                        {!inlineClientId && (
                            <Controls.PremiumButton
                                variant="glass"
                                label="Wyczyść"
                                onClick={clearFilters}
                                icon="fas fa-filter"
                            />
                        )}
                        {isGenerateInvoicesAllowed && (
                            <Controls.PremiumButton
                                variant="green"
                                label={generatingInvoices ? 'Generowanie...' : 'Generuj faktury'}
                                onClick={handleGenerateInvoices}
                                disabled={generatingInvoices}
                                icon={generatingInvoices ? "fas fa-spinner fa-spin" : "fas fa-file-invoice-dollar"}
                            />
                        )}
                    </Box>
                </Box>
                {!inlineClientId && (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
                        <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)} textColor="primary" indicatorColor="primary">
                            <Tab label="Sprawozdania finansowe" />
                            {(isSupervisor || roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR') || roles?.includes('MANAGER') || roles?.includes('ACCOUNT_MANAGER')) && <Tab label="Faktury do sprawozdań" />}
                        </Tabs>
                    </Box>
                )}

                {currentTab === 0 && (
                    <>
                        {currentModeIsAccountant() ? (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        p: 2,
                                        mb: 1,
                                        borderRadius: '12px',
                                        background: darkTheme
                                            ? 'linear-gradient(135deg, #1e1e1e 0%, #2e2e2e 100%)'
                                            : 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
                                        border: darkTheme ? '1px solid #444' : '1px solid #ffeeba',
                                        boxShadow: darkTheme
                                            ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                                            : '0 4px 12px rgba(0, 0, 0, 0.05)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <i className="fas fa-info-circle" style={{ color: darkTheme ? '#feb33c' : '#856404', fontSize: '1.1rem' }}></i>
                                        <Typography variant="body2" sx={{ color: darkTheme ? '#ffe58f' : '#856404', fontWeight: 500 }}>
                                            Osobną Fakturę za sprawozdania finansowe <b>{selectedYear}</b> prosimy wystawić <b>ZBIORCZO</b>, gdy wszystkie sprawozdania już będą gotowe.
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <i className="fas fa-paperclip" style={{ color: darkTheme ? '#feb33c' : '#856404', fontSize: '1.1rem' }}></i>
                                        <Typography variant="body2" sx={{ color: darkTheme ? '#ffe58f' : '#856404', fontWeight: 500 }}>
                                            Fakturę należy wgrać korzystając z przycisku obok, po zakończeniu wszystkich sprawozdań.
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1.5} sx={{ mt: 0.5, p: '8px 12px', borderRadius: '8px', bgcolor: darkTheme ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.05)' }}>
                                        <i className="fas fa-gift" style={{ color: darkTheme ? '#81c784' : '#2e7d32', fontSize: '1.1rem' }}></i>
                                        <Typography variant="body2" sx={{ color: darkTheme ? '#81c784' : '#1b5e20', fontWeight: 700, flex: 1 }}>
                                            Premia za terminowość: 100 zł netto do sprawozdania (jeżeli wgrasz do systemu najpóźniej do 31.03.{parseInt(selectedYear) + 1}).
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            component="label"
                                            color="success"
                                            disabled={isInvoiceUploaded}
                                            startIcon={<i className={`fas ${isInvoiceUploaded ? 'fa-check-circle' : 'fa-upload'}`}></i>}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                                        >
                                            {isInvoiceUploaded ? 'Faktura wgrana' : 'Wgraj fakturę'}
                                            {!isInvoiceUploaded && (
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    onChange={handleInvoiceUpload}
                                                />
                                            )}
                                        </Button>

                                        {/* Przycisk do wgrania korekty (widoczny tylko gdy zażądano) */}
                                        {isInvoiceUploaded && accountantInvoice?.correctionRequested && (
                                            <Button
                                                variant="contained"
                                                component="label"
                                                color="warning"
                                                disabled={!!(accountantInvoice?.correctionFileMetadata && accountantInvoice.correctionFileMetadata.id)}
                                                startIcon={<i className={`fas ${accountantInvoice?.correctionFileMetadata?.id ? 'fa-check-circle' : 'fa-upload'}`}></i>}
                                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', ml: 1 }}
                                            >
                                                {accountantInvoice?.correctionFileMetadata?.id ? 'Korekta wgrana' : 'Wgraj korektę'}
                                                {(!accountantInvoice?.correctionFileMetadata || !accountantInvoice.correctionFileMetadata.id) && (
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        onChange={handleCorrectionUpload}
                                                    />
                                                )}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        ) : null}
                        <Box sx={{ m: '20px' }} />
                        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
                            <Grid item xs={12}>
                                <div className="App">
                                    <div className="tableData" ref={tableDataRef}>
                                        <div
                                            className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                                            style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                                        >
                                            <AgGridReact
                                                key={darkTheme ? 'grid-dark' : 'grid-light'}
                                                rowData={filteredData}
                                                columnDefs={columnDefs}
                                                defaultColDef={defaultColDef}
                                                onGridReady={onGridReady}
                                                onCellValueChanged={handleRowDataChange}
                                                getRowId={getRowId}
                                                ref={gridRef}
                                                pagination={true}
                                                paginationPageSize={25}
                                                singleClickEdit={true}
                                                suppressBrowserResizeObserver={true}
                                                animateRows={true}
                                                rowSelection={false}
                                                suppressSizeToFit={true}
                                                enableBrowserTooltips={false}
                                                localeText={MyCollections.localeTextPL}
                                                tooltipShowDelay={0}
                                                popupParent={popupParent}
                                                getRowHeight={() => 38}
                                                onFirstDataRendered={onFirstDataRendered}
                                                overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                                                overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                                                onFilterChanged={() => setRecalcVersion(v => v + 1)}
                                                getRowStyle={(params) => {
                                                    const status = params.data?.toDoStatus;

                                                    // Jeżeli status to "Nie robimy" (NOT_DOING), to przyciemniamy i wyszarzamy
                                                    if (status === 'NOT_DOING') {
                                                        return {
                                                            opacity: 0.5,
                                                            filter: 'grayscale(60%)',
                                                        };
                                                    }

                                                    return undefined;
                                                }}
                                            />
                                        </div>
                                        {/* NOWA STRUKTURA SUMUJĄCA */}
                                        <Box
                                            sx={{
                                                mt: 0.5,
                                                p: 2,
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: 6,
                                                background: darkTheme ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                                                borderRadius: '0 0 12px 12px',
                                                border: darkTheme ? '1px solid #444' : '1px solid #eee',
                                                borderTop: 'none',
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {!(isAccountant || isSupervisor) && (
                                                <Box textAlign="right" sx={{ pr: 6, borderRight: darkTheme ? '1px solid #555' : '1px solid #ccc' }}>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Suma od Klientów</Typography>
                                                    <Typography variant="h5" sx={{ fontWeight: 800, color: darkTheme ? '#ce93d8' : '#9c27b0' }}>
                                                        {clientTotal.toLocaleString('pl-PL')} <span style={{ fontSize: '14px' }}>zł brutto</span>
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box textAlign="right">
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Suma za wykonane sprawozdania</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: darkTheme ? '#90caf9' : '#1976d2' }}>
                                                    {baseSalary.toLocaleString('pl-PL')} <span style={{ fontSize: '14px' }}>zł netto</span>
                                                </Typography>
                                            </Box>
                                            <Box textAlign="right">
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Suma Premii</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: darkTheme ? '#ffb74d' : '#f57c00' }}>
                                                    {bonusAmount.toLocaleString('pl-PL')} <span style={{ fontSize: '14px' }}>zł netto</span>
                                                </Typography>
                                            </Box>
                                            <Box textAlign="right" sx={{ borderLeft: darkTheme ? '1px solid #555' : '1px solid #ccc', pl: 6 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Razem do wypłaty</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#d32f2f' }}>
                                                    {(baseSalary + bonusAmount).toLocaleString('pl-PL')} <span style={{ fontSize: '14px' }}>zł netto</span>
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </div>
                                </div>

                                <Dialog open={open} onClose={handleCancel}>
                                    <DialogTitle>
                                        {pendingChange?.type === "settled"
                                            ? "Potwierdzenie zmiany statusu rozliczenia"
                                            : pendingChange?.type === "clientSettled"
                                                ? "Potwierdzenie zmiany statusu rozliczenia z klientem"
                                                : "Potwierdzenie zmiany osoby robiącej sprawozdanie"}
                                    </DialogTitle>

                                    <DialogContent>
                                        <Typography variant="body2">
                                            {pendingChange?.type === "settled" || pendingChange?.type === "clientSettled" ? (
                                                <>
                                                    Czy na pewno chcesz{" "}
                                                    {pendingChange?.newValue
                                                        ? "oznaczyć jako rozliczone"
                                                        : "cofnąć rozliczenie"}{" "}
                                                    {pendingChange?.type === "clientSettled" ? "z klientem " : "z księgowym "}
                                                    dla klienta <b>{pendingChange?.companyName}</b>?
                                                </>
                                            ) : (
                                                <>
                                                    Czy na pewno chcesz zmienić osobę robiącą sprawozdanie dla klienta <b>{pendingChange?.companyName} </b> z
                                                    <b> {pendingChange?.fromLabel}</b> na <b>{pendingChange?.toLabel}</b>?
                                                </>
                                            )}
                                        </Typography>
                                    </DialogContent>

                                    <DialogActions>
                                        <Controls.PremiumButton variant="glass" onClick={handleCancel} label="Anuluj" />
                                        <Controls.PremiumButton
                                            variant="green"
                                            onClick={handleConfirm}
                                            label="Potwierdź"
                                        />
                                    </DialogActions>
                                </Dialog>
                            </Grid>
                        </Grid>
                    </>
                )}

                {currentTab === 1 && (
                    <Box p={2}>
                        <AccountantFinancialStatementInvoicesGrid
                            year={selectedYear}
                            token={token}
                            darkTheme={darkTheme}
                            isSupervisorMode={isSupervisorMode}
                            isSupervisorAccountant={isSupervisorAccountant}
                            isAccountant={isAccountant}
                            roles={roles}
                            userName={userName}
                            contactId={contactId}
                        />
                    </Box>
                )}
            </Paper>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={snackbarSeverity === 'warning' ? 10000 : 4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <ConfirmDeleteDialog
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={confirmDelete}
            />
            <FormDialog open={clientCardOpen} handleClose={() => handleClose()}
                onChange={onChange} handleFormSubmit={handleFormSubmit} setFormData={setFormData} formData={formData} />
            <GenerateFinancialStatementsInvoiceDialog
                open={openInvoiceConfirm}
                handleClose={() => setOpenInvoiceConfirm(false)}
                handleConfirm={executeGenerateInvoices}
                year={selectedYear}
                isGenerating={generatingInvoices}
            />
            <UnsignedStatementModal
                open={unsignedUploadModal.open}
                statementFile={unsignedUploadModal.statementFile}
                onCancel={() => setUnsignedUploadModal({ open: false, rowId: null, clientId: null, statementFile: null })}
                onConfirm={handleUnsignedUploadConfirm}
                token={token}
                darkTheme={darkTheme}
            />
        </>
    );
}

export default FinancialStatements;